<template>
  <div class="booking-share-panel">
    <label class="checkbox">
      <input
        type="checkbox"
        :checked="shareUiEnabled"
        @change="onShareToggle($event)"
      >
      Offentlig delingsside
    </label>
    <p class="help booking-share__help">
      Gør turen synlig via et link, du kan dele på SoMe — fx hvis du tager folk med.
    </p>
    <div v-if="shareUiEnabled" class="booking-share__note">
      <label for="shareNote" class="label">Destination (hvortil)</label>
      <input
        id="shareNote"
        ref="shareNote"
        class="input"
        type="text"
        :maxlength="DESTINATION_MAX_LEN"
        placeholder="Fx Aarhus, Ikea eller Skanderborg"
        v-model="shareNoteInput"
        :disabled="publicToggleSubmitting"
      >
      <p class="help">Krævet for offentlig deling. Vises i titel og på delingssiden.</p>
      <div class="booking-share__actions">
        <button
          v-if="shareNeedsSave"
          type="button"
          class="button is-info is-small"
          :disabled="publicToggleSubmitting || !shareNoteInput.trim()"
          @click="saveSharePublic"
        >
          {{ publicToggleSubmitting ? 'Gemmer…' : 'Gem deling' }}
        </button>
        <button
          v-if="booking.isPublic"
          type="button"
          class="button is-success booking-share__copy"
          :class="{ 'is-copied': shareLinkCopied }"
          @click="copyShareLink"
        >
          <span class="icon">
            <Icon :name="shareLinkCopied ? 'check' : 'link'" />
          </span>
          <span>{{ shareLinkCopied ? 'Link kopieret!' : 'Kopiér delingslink' }}</span>
        </button>
      </div>
      <p v-if="booking.isPublic && !shareNeedsSave" class="help booking-share__copy-hint">
        Del linket på SoMe, så andre kan se turen og tage med.
      </p>
    </div>
    <article v-if="publicToggleError" class="message is-danger booking-share__error">
      <div class="message-body">{{ publicToggleError }}</div>
    </article>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import ax from 'axios'
import Icon from '@/components/Icon.vue'

const DESTINATION_MAX_LEN = 40

export default defineComponent({
  name: 'BookingSharePanel',
  components: { Icon },
  props: {
    booking: {
      type: Object,
      required: true
    },
    isOwnAccount: {
      type: Boolean,
      default: false
    }
  },
  emits: ['share-updated'],
  data() {
    return {
      DESTINATION_MAX_LEN,
      publicToggleSubmitting: false,
      publicToggleError: '',
      shareLinkCopied: false,
      shareDraftEnabled: false,
      shareNoteInput: ''
    }
  },
  watch: {
    'booking.id'() {
      this.syncFromBooking()
    },
    'booking.shareNote'() {
      if (!this.shareDraftEnabled && !this.publicToggleSubmitting) {
        this.shareNoteInput = this.booking.shareNote || ''
      }
    }
  },
  created() {
    this.syncFromBooking()
  },
  computed: {
    shareUiEnabled() {
      return !!this.booking.isPublic || this.shareDraftEnabled
    },
    shareNeedsSave() {
      if (!this.shareUiEnabled) {
        return false
      }
      if (this.shareDraftEnabled && !this.booking.isPublic) {
        return true
      }
      const current = (this.shareNoteInput || '').trim()
      const saved = (this.booking.shareNote || '').trim()
      return !!this.booking.isPublic && current !== saved
    }
  },
  methods: {
    syncFromBooking() {
      this.shareNoteInput = this.booking.shareNote || ''
      this.shareDraftEnabled = false
      this.publicToggleError = ''
      this.shareLinkCopied = false
    },
    onShareToggle(event) {
      if (this.publicToggleSubmitting || !this.isOwnAccount) {
        return
      }

      this.publicToggleError = ''
      const wantPublic = !!event.target.checked

      if (!wantPublic) {
        this.shareDraftEnabled = false
        if (!this.booking.isPublic) {
          return
        }
        this.setSharePublic(false)
        return
      }

      this.shareDraftEnabled = true
      if (!this.shareNoteInput.trim() && this.booking.shareNote) {
        this.shareNoteInput = this.booking.shareNote
      }
      this.$nextTick(() => {
        if (this.$refs.shareNote) {
          this.$refs.shareNote.focus()
        }
      })
    },
    saveSharePublic() {
      const note = (this.shareNoteInput || '').trim()
      if (!note) {
        this.publicToggleError = 'Angiv destination (hvortil), før delingen kan være offentlig.'
        return
      }
      this.setSharePublic(true, note)
    },
    parseShareResponse(data) {
      if (data && typeof data === 'object') {
        return data
      }
      if (typeof data === 'string') {
        try {
          return JSON.parse(data)
        } catch (e) {
          return null
        }
      }
      return null
    },
    emitShareUpdate(isPublic, shareNote) {
      this.$emit('share-updated', {
        isPublic,
        shareNote
      })
    },
    setSharePublic(isPublic, shareNote) {
      if (this.publicToggleSubmitting) {
        return
      }

      const previousPublic = !!this.booking.isPublic
      const previousNote = this.booking.shareNote || ''
      const previousDraft = this.shareDraftEnabled
      const note = (shareNote !== undefined ? shareNote : this.shareNoteInput || '').trim()

      this.publicToggleSubmitting = true
      this.publicToggleError = ''

      if (isPublic) {
        this.emitShareUpdate(true, note)
        this.shareDraftEnabled = true
      }

      ax.get(
        process.env.VUE_APP_API_ROOT +
          '/booking.php?mode=setPublic&id=' + this.booking.id +
          '&isPublic=' + (isPublic ? '1' : '0') +
          '&shareNote=' + encodeURIComponent(note)
      )
        .then((response) => {
          const data = this.parseShareResponse(response.data)
          if (!data || data.error) {
            this.emitShareUpdate(previousPublic, previousNote)
            this.shareNoteInput = previousNote
            this.shareDraftEnabled = previousDraft || (!previousPublic && isPublic)
            if (data && data.alias === 'ShareNoteRequired') {
              this.publicToggleError = 'Angiv destination (hvortil), før delingen kan være offentlig.'
            } else {
              this.publicToggleError = 'Kunne ikke opdatere deling. Prøv igen.'
            }
            return
          }
          const nextPublic = data.isPublic === true || data.isPublic === 1 || data.isPublic === '1'
          const nextNote = data.shareNote != null ? String(data.shareNote) : note
          this.emitShareUpdate(nextPublic, nextNote)
          this.shareNoteInput = nextNote || ''
          this.shareDraftEnabled = false
          if (!nextPublic) {
            this.shareLinkCopied = false
          }
        })
        .catch(() => {
          this.emitShareUpdate(previousPublic, previousNote)
          this.shareNoteInput = previousNote
          this.shareDraftEnabled = previousDraft || (!previousPublic && isPublic)
          this.publicToggleError = 'Kunne ikke opdatere deling. Prøv igen.'
        })
        .finally(() => {
          this.publicToggleSubmitting = false
        })
    },
    copyShareLink() {
      const url = window.location.origin + '/booking/deling/' + this.booking.id
      const showCopied = () => {
        this.shareLinkCopied = true
        setTimeout(() => {
          this.shareLinkCopied = false
        }, 2000)
      }

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(showCopied).catch(() => {
          this.fallbackCopyShareLink(url, showCopied)
        })
        return
      }

      this.fallbackCopyShareLink(url, showCopied)
    },
    fallbackCopyShareLink(url, onSuccess) {
      const input = document.createElement('textarea')
      input.value = url
      input.setAttribute('readonly', '')
      input.style.position = 'absolute'
      input.style.left = '-9999px'
      document.body.appendChild(input)
      input.select()
      try {
        document.execCommand('copy')
        onSuccess()
      } catch (e) {
        // ignore
      }
      document.body.removeChild(input)
    }
  }
})
</script>

<style scoped>
.booking-share__help {
  margin-top: 0.35rem;
  margin-bottom: 0;
  max-width: 28rem;
}

.booking-share__note {
  margin-top: 0.75rem;
  max-width: 28rem;
}

.booking-share__note .label {
  font-size: 0.75rem;
  margin-bottom: 0.25rem;
}

.booking-share__actions {
  margin-top: 0.65rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
}

.booking-share__copy {
  font-weight: 600;
}

.booking-share__copy.is-copied {
  background-color: #2F6B2F;
}

.booking-share__copy-hint {
  margin-top: 0.4rem;
  margin-bottom: 0;
  color: #2F6B2F;
  font-weight: 600;
}

.booking-share__error {
  margin-top: 0.65rem;
  margin-bottom: 0;
}
</style>
